<template>
  <footer class="app-footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-info">
          <div class="footer-logo">
            <img :src="footerLogoSrc" mode="aspectFit" alt="海旗科技平台" class="footer-logo-img" @error="handleLogoError" />
          </div>
          <div class="contact-info">
            <p class="service-description">提供优质课程教学、第二专业系列课程教学、高端培训系列课程以及相关在线教育产品。</p>
          </div>
        </div>

        <div class="footer-links">
          <div class="link-group">
            <h3>关于我们</h3>
            <router-link v-for="doc in aboutUsDocs" :key="doc.id" :to="`/document/${doc.key}`">{{ doc.title }}</router-link>
          </div>

          <div class="link-group">
            <h3>常见问题</h3>
            <router-link v-for="doc in commonProblems" :key="doc.id" :to="`/document/${doc.key}`">{{ doc.title }}</router-link>
          </div>

          <div class="link-group">
            <h3>友情链接</h3>
            <a href="https://www.yuketang.cn/" target="_blank" rel="noopener noreferrer">雨课堂</a>
            <a href="https://www.xuetangx.com/" target="_blank" rel="noopener noreferrer">学堂在线</a>
            <a href="http://www.hanweiguoonline.com/" target="_blank" rel="noopener noreferrer">书法在线</a>
          </div>

          <div class="link-group">
            <h3>联系我们</h3>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>
          ICP备案号：<a :href="'https://beian.miit.gov.cn/'" target="_blank" rel="noopener noreferrer">{{ icpNumber }}</a>
          <span v-if="licenseNumber"> | </span>
          <span v-if="licenseNumber">公网安备案号：<a :href="'https://www.beian.gov.cn/'" target="_blank" rel="noopener noreferrer">{{ licenseNumber }}</a></span>
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useSchoolStore } from '@/stores/school'
import request from '@/utils/request'

export default {
  name: 'AppFooter',
  setup() {
    const schoolStore = useSchoolStore()
    // 动态获取，如果未获取到，则使用如下的默认图片
    const defaultLogoSrc = `${process.env.BASE_URL}commonPic/transparenBottomLogo.png`
    const icpNumber = ref('桂ICP备2025077793号-1') // 默认值
    const licenseNumber = ref('') // 公网安备案号，默认空
    const commonProblems = ref([]) // 常见问题文档
    const aboutUsDocs = ref([]) // 关于我们文档

    const normalizeText = (value) => String(value ?? '').replace(/`/g, '').trim()
    const normalizeLogoUrl = (value) => {
      const raw = normalizeText(value)
      if (!raw) return ''
      if (/^(https?:)?\/\//i.test(raw)) return raw
      if (/^\/\//.test(raw)) return `https:${raw}`
      if (raw.startsWith('/')) return raw
      return raw
    }

    const footerLogoSrc = computed(() => {
      const logo = normalizeLogoUrl(schoolStore.getCurrentSchool?.banner)
      return logo || defaultLogoSrc
    })

    const handleLogoError = (event) => {
      event.target.src = defaultLogoSrc
    }

    // 从接口获取ICP备案号和公网安备案号
    const fetchIcpNumber = async () => {
      try {
        const response = await request({
          url: '/course/settings_info',
          method: 'get'
        })
        if (response.code === 200 && response.data) {
          if (response.data.icp) {
            icpNumber.value = response.data.icp
          }
          if (response.data.license) {
            licenseNumber.value = response.data.license
          }
        }
      } catch (error) {
        console.error('获取备案号失败:', error)
      }
    }

    // 从接口获取文档数据
    const fetchDocumentList = async () => {
      try {
        const response = await request({
          url: '/course/document_list',
          method: 'get',
          params: {
            PageNum: 1,
            PageSize: 50
          }
        })
        if (response.code === 200 && response.data) {
          // 筛选docType为1的文档（常见问题），只取前3条
          commonProblems.value = response.data.filter(item => item.docType === 1).slice(0, 3)
          // 筛选docType为2的文档（关于我们），只取前3条
          aboutUsDocs.value = response.data.filter(item => item.docType === 2).slice(0, 3)
        }
      } catch (error) {
        console.error('获取文档数据失败:', error)
      }
    }

    // 页面挂载时获取数据
    onMounted(() => {
      fetchIcpNumber()
      fetchDocumentList()
    })

    return { footerLogoSrc, handleLogoError, icpNumber, licenseNumber, commonProblems, aboutUsDocs }
  }
}
</script>

<style scoped>
.app-footer {
  background-color: #333;
  color: #fff;
  padding: 3rem 0 1rem;
  z-index: 888;
}

.footer-content {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.footer-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.footer-logo{
  display: flex;
  justify-content: center;
}
.footer-logo-img {
  height: 100px; /* 设置合适的高度 */
  width: auto; /* 保持宽高比 */
  margin-bottom: 0.5rem;
}

.service-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #ccc;
  margin-bottom: 0.5rem;
}

.contact-info {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }

.footer-links {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.link-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.link-group h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.link-group a {
  color: #ccc;
  text-decoration: none;
  transition: color 0.3s ease;
}

.link-group a:hover {
  color: var(--primary-color);
}

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 1.5rem;
  /* border-top: 1px solid rgba(255, 255, 255, 0.1); */
}

.footer-bottom a {
  color: #fff;
  text-decoration: none;
}

.footer-bottom a:hover {
  color: #fff;
  text-decoration: none;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-links a {
  color: #fff;
  font-size: 1.2rem;
  transition: color 0.3s ease;
}

.social-links a:hover {
  color: var(--primary-color);
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
  }

  .footer-links {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

@media (max-width: 480px) {
  .footer-links {
    grid-template-columns: 1fr;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
